import * as THREE from 'three';
import { WallType, WallShapeType, WallConnectionStart, WallConnectionEnd, WallEdgeConnection, JointPolygonData, OutlineSegmentCollection } from '../types/floorplannerTypes';

// Declare window interface for memory warning tracking
declare global {
  interface Window {
    __wallUpdateMemoryWarnings?: {
      lastWarning?: number;
      lastProgressiveWarning?: number;
    };
  }
}
import { WallUpdateManager as WallUpdateManagerType, SimplifiedWallGeometry, CachedJointPolygon, CachedOutlineSegments } from '../types/wallCacheTypes';
import { WallJointCacheManager } from './wallJointCache';
import { WallDependencyGraph } from './wallDependencyGraph';
import { WallChangeDetector } from './wallChangeDetection';
import { jointDetector, JointInfo } from './JointDetector';
import { floorplannerStore } from '../store/floorplannerStore';
import { renderStore } from '../store/renderStore';
import { editorStore } from '../store/editorStore';
import PerformanceMonitor from './PerformanceMonitor';
import { wallGeometryCache } from './WallGeometryCache';
import DragStateManager from './DragStateManager';
import { isFloorplannerDebugLoggingEnabled } from './debugLogging';

// Extend Performance interface for memory property
declare global {
  interface Performance {
    memory?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
  }
}

// Helper function to safely extract error messages from unknown error types
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as any).message);
  }
  return 'Unknown error occurred';
};

export class WallUpdateManager implements WallUpdateManagerType {
  public dependencyGraph = new Map<string, Set<string>>();
  public jointCache = new Map<string, any>();
  public spatialIndex = new Map<string, Set<string>>();
  public pendingUpdates = new Set<string>();
  public updateBatchTimeout: number | null = null;

  private cacheManager = new WallJointCacheManager();
  private dependencyGraphManager = new WallDependencyGraph();
  private isProcessingUpdates = false;
  private batchDelay = 16; // ~60fps
  private isProgressiveMode = false;
  private progressiveQuality = 1.0;
  private progressiveWalls = new Set<string>(); // Track which walls are in progressive mode
  private jointDetector = jointDetector;
  private jointInfoCache = new Map<string, JointInfo[]>();
  private watchdogTimer: number | null = null;
  private watchdogTimeout = 2000; // 2 seconds of inactivity
  private processingWaiters: Array<() => void> = [];

  private getConnectedWallIds = (wall: WallType): string[] => {
    const connectedIds = new Set<string>();

    wall.centerConnections?.forEach(connection => {
      if (connection?.id && connection.id !== wall.id) {
        connectedIds.add(connection.id);
      }
    });

    wall.edgeConnections?.forEach(edgeConnection => {
      if (edgeConnection?.targetWallId && edgeConnection.targetWallId !== wall.id) {
        connectedIds.add(edgeConnection.targetWallId);
      }
    });

    return Array.from(connectedIds);
  }

  private getConnectedWalls = (wall: WallType): WallType[] => {
    return this.getConnectedWallIds(wall)
      .map(id => floorplannerStore.wallsMap.get(id))
      .filter((connectedWall): connectedWall is WallType => Boolean(connectedWall));
  }

  // Performance monitoring properties
  private activeDragOperations = new Map<string, string>(); // wallId -> operationId mapping
  private wallUpdateFrequencies = new Map<string, number>(); // wallId -> update count

  // Cache performance metrics
  private simplifiedGeometryCacheHits = 0;
  private simplifiedGeometryCacheMisses = 0;
  private jointPolygonCacheHits = 0;
  private jointPolygonCacheMisses = 0;
  private outlineSegmentCacheHits = 0;
  private outlineSegmentCacheMisses = 0;

  /**
   * Initialize the update manager
   */
  initialize(): void {
    this.buildDependencyGraph();
  }

  /**
   * Build initial dependency graph
   */
  buildDependencyGraph(): void {
    this.dependencyGraphManager.buildDependencyGraph(new Map(floorplannerStore.wallsMap));
    this.dependencyGraph = this.dependencyGraphManager['dependencyGraph'];
    this.spatialIndex = this.dependencyGraphManager['spatialIndex'];

    // Always add edge connections to dependency graph
    this.addEdgeConnectionsToDependencyGraph();
  }

  /**
   * Add edge connections to the dependency graph for efficient invalidation.
   * This ensures that when a wall moves, all edge-connected walls are also updated.
   */
  private addEdgeConnectionsToDependencyGraph(): void {
    // Iterate through all walls
    floorplannerStore.wallsMap.forEach((wall, wallId) => {
      if (!wall.edgeConnections) return;

      // Get or create dependency set for this wall
      let dependencies = this.dependencyGraph.get(wallId);
      if (!dependencies) {
        dependencies = new Set();
        this.dependencyGraph.set(wallId, dependencies);
      }

      // Add edge-connected walls as dependencies
      for (const edgeConn of wall.edgeConnections) {
        dependencies.add(edgeConn.targetWallId);

        // Add bidirectional dependency
        let reverseDependencies = this.dependencyGraph.get(edgeConn.targetWallId);
        if (!reverseDependencies) {
          reverseDependencies = new Set();
          this.dependencyGraph.set(edgeConn.targetWallId, reverseDependencies);
        }
        reverseDependencies.add(wallId);
      }
    });
  }

  /**
   * Refresh edge connection dependencies for a specific wall.
   * Removes existing edge-related dependencies and re-adds them based on current edge connections.
   * Called when edge connections change to keep the dependency graph accurate.
   */
  private refreshEdgeConnectionDependencies(wallId: string): void {
    const wall = floorplannerStore.wallsMap.get(wallId);
    if (!wall) return;

    // Get current dependencies for this wall
    const currentDependencies = this.dependencyGraph.get(wallId);

    // Collect walls that should be in the dependency set based on edge connections
    const newEdgeConnectedWalls = new Set<string>();
    if (wall.edgeConnections) {
      wall.edgeConnections.forEach(edgeConn => {
        newEdgeConnectedWalls.add(edgeConn.targetWallId);

        // Add bidirectional dependency
        let reverseDependencies = this.dependencyGraph.get(edgeConn.targetWallId);
        if (!reverseDependencies) {
          reverseDependencies = new Set();
          this.dependencyGraph.set(edgeConn.targetWallId, reverseDependencies);
        }
        reverseDependencies.add(wallId);
      });
    }

    // Update dependencies for this wall
    if (currentDependencies) {
      // Remove old edge-related dependencies
      // We need to identify which dependencies are from edge connections vs other sources
      // For now, we'll rebuild the entire dependency set based on edge connections
      // This is safe because the regular dependency graph manager handles non-edge dependencies
      this.dependencyGraph.set(wallId, newEdgeConnectedWalls);
    } else {
      // No existing dependencies, create new set
      this.dependencyGraph.set(wallId, newEdgeConnectedWalls);
    }
  }

  /**
   * Update wall with smart dependency-based updates
   */
  updateWall(wallId: string, forceRecompute: boolean = false): void {
    // Add to pending updates
    this.pendingUpdates.add(wallId);

    // Reset watchdog timer since we have activity
    this.resetWatchdog();

    // Schedule batched update
    this.scheduleBatchUpdate();
  }

  /**
   * Schedule batched update processing
   */
  private scheduleBatchUpdate(): void {
    if (this.updateBatchTimeout) {
      clearTimeout(this.updateBatchTimeout);
    }

    this.updateBatchTimeout = window.setTimeout(() => {
      this.processBatchedUpdates();
    }, this.batchDelay);
  }

  /**
   * Process all pending updates in batch
   */
  private async processBatchedUpdates(): Promise<void> {
    if (this.isProcessingUpdates || this.pendingUpdates.size === 0) return;

    this.isProcessingUpdates = true;
    const updatesToProcess = Array.from(this.pendingUpdates);
    this.pendingUpdates.clear();
    const startedAt = performance.now();

    try {
      // Group walls by their dependency groups
      const updateGroups = this.groupUpdatesByDependency(updatesToProcess);

      // Process each group
      for (const group of updateGroups) {
        await this.processUpdateGroup(group);
      }
      console.log('[load] 2D processBatchedUpdates', {
        ms: Math.round(performance.now() - startedAt),
        walls: updatesToProcess.length,
        groups: updateGroups.length,
      });
    } finally {
      this.isProcessingUpdates = false;
      const waiters = this.processingWaiters.splice(0);
      waiters.forEach(resolve => resolve());
    }
  }

  private waitForCurrentProcessing(): Promise<void> {
    if (!this.isProcessingUpdates) {
      return Promise.resolve();
    }

    return new Promise(resolve => {
      this.processingWaiters.push(() => resolve());
    });
  }

  /**
   * Group updates by their dependency relationships
   */
  private groupUpdatesByDependency(wallIds: string[]): string[][] {
    const groups: string[][] = [];
    const processed = new Set<string>();

    for (const wallId of wallIds) {
      if (processed.has(wallId)) continue;

      const group = this.getDependencyGroup(wallId, wallIds);
      group.forEach(id => processed.add(id));
      groups.push(group);
    }

    return groups;
  }

  /**
   * Get all walls in the same dependency group
   */
  private getDependencyGroup(wallId: string, candidateIds: string[]): string[] {
    const group = new Set<string>();
    const queue = [wallId];
    const candidates = new Set(candidateIds);

    while (queue.length > 0) {
      const current = queue.shift()!;
      if (group.has(current) || !candidates.has(current)) continue;

      group.add(current);

      // Add walls that depend on this wall
      this.dependencyGraph.forEach((deps, depWallId) => {
        if (deps.has(current) && candidates.has(depWallId) && !group.has(depWallId)) {
          queue.push(depWallId);
        }
      });

      // Add walls this wall depends on
      const dependencies = this.dependencyGraph.get(current);
      if (dependencies) {
        dependencies.forEach(depId => {
          if (candidates.has(depId) && !group.has(depId)) {
            queue.push(depId);
          }
        });
      }
    }

    return Array.from(group);
  }

  /**
   * Process a group of related wall updates
   *
   * Enhanced with detailed logging for progressive vs normal mode operations
   * and performance metrics tracking for progressive mode effectiveness.
   */
  private async processUpdateGroup(wallIds: string[]): Promise<void> {
    const startTime = performance.now();
    const operationId = `processUpdateGroup_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    let updateErrors = 0;
    let progressiveWallsProcessed = 0;
    let normalWallsProcessed = 0;

    // Track update frequencies for each wall
    wallIds.forEach(wallId => {
      const currentCount = this.wallUpdateFrequencies.get(wallId) || 0;
      this.wallUpdateFrequencies.set(wallId, currentCount + 1);
      PerformanceMonitor.trackDragEvent(wallId, 'wall_update_frequency', 1);
    });

    try {
      // Track operation start
      PerformanceMonitor.trackDragEvent('wallUpdateManager', 'operation_start', 1);

      // Check if any of the walls are in progressive mode (being dragged)
      const hasProgressiveWalls = wallIds.some(wallId => this.progressiveWalls.has(wallId));

      if (hasProgressiveWalls) {
        PerformanceMonitor.trackDragEvent('wallUpdateManager', 'progressive_operation', 1);
      }

      // Track memory usage before operation
      const memoryBefore = performance.memory ? performance.memory.usedJSHeapSize / (1024 * 1024) : 0;

      let allAffectedWalls: Set<string>;
      const dependencyCalculationStart = performance.now();

      try {
        if (hasProgressiveWalls) {
          // Progressive mode: Include dragged walls and their immediate neighbors only
          // Use the progressive wall set as the seed rather than all walls in the group
          allAffectedWalls = new Set<string>();

          // First, add all dragged/progressive wall IDs to the affected set
          wallIds.forEach(wallId => {
            if (this.progressiveWalls.has(wallId)) {
              allAffectedWalls.add(wallId);
            }
          });

          // Then add their immediate neighbors only
          wallIds.forEach(wallId => {
            if (this.progressiveWalls.has(wallId)) {
              try {
                const directlyConnected = this.dependencyGraphManager.getDirectlyConnectedWalls(wallId);
                directlyConnected.forEach(id => allAffectedWalls.add(id));
              } catch (dependencyError) {
                console.error('wallUpdateManager: Error getting directly connected walls', {
                  wallId,
                  operationId,
                  error: getErrorMessage(dependencyError)
                });
                PerformanceMonitor.trackError('wallUpdateManager', 'dependency_calculation', 'calculation_error');
                updateErrors++;
              }
            }
          });

          // Filter out walls that DragStateManager says should be suppressed
          // This prevents cascading updates to unrelated walls during middle handle drags
          if (DragStateManager.isSuppressingAllWallUpdates()) {
            const filteredWalls = new Set<string>();
            allAffectedWalls.forEach(wallId => {
              if (DragStateManager.isWallAllowedToUpdate(wallId)) {
                filteredWalls.add(wallId);
              }
            });
            allAffectedWalls = filteredWalls;
          }

          if (isFloorplannerDebugLoggingEnabled()) {
            console.debug(`Progressive mode: processing ${wallIds.length} walls, ${allAffectedWalls.size} affected`);
          }
        } else {
          // Normal mode: get all affected walls (including transitive dependencies)
          allAffectedWalls = new Set<string>();
          wallIds.forEach(wallId => {
            try {
              const affected = this.dependencyGraphManager.getAffectedWalls(wallId);
              affected.forEach(id => allAffectedWalls.add(id));
            } catch (dependencyError) {
              console.error('wallUpdateManager: Error getting affected walls', {
                wallId,
                operationId,
                error: getErrorMessage(dependencyError)
              });
              PerformanceMonitor.trackError('wallUpdateManager', 'dependency_calculation', 'calculation_error');
              updateErrors++;
            }
          });

          if (isFloorplannerDebugLoggingEnabled()) {
            console.debug(`Normal mode: processing ${wallIds.length} walls, ${allAffectedWalls.size} affected`);
          }
        }

        const dependencyCalculationTime = performance.now() - dependencyCalculationStart;
        PerformanceMonitor.trackDragEvent('wallUpdateManager', 'dependency_calculation', dependencyCalculationTime);

        if (dependencyCalculationTime > 20) {
          // Slow dependency calculation - warning removed
        }
      } catch (overallDependencyError) {
        console.error('wallUpdateManager: Critical error in dependency calculation', {
          operationId,
          error: getErrorMessage(overallDependencyError),
          wallIds
        });
        PerformanceMonitor.trackError('wallUpdateManager', 'dependency_calculation', 'critical_error');
        return; // Cannot proceed without dependency information
      }

      // Wall update phase
      const wallUpdateStart = performance.now();

      try {
        if (hasProgressiveWalls) {
          // Update walls with targeted progressive mode - only progressive walls get simplified calculations
          for (const wallId of allAffectedWalls) {
            try {
              if (this.progressiveWalls.has(wallId)) {
                await this.updateWallShapeProgressive(wallId);
                progressiveWallsProcessed++;
              } else {
                // Normal calculations for non-progressive walls
                await this.updateWallShape(wallId);
                normalWallsProcessed++;
              }
            } catch (wallUpdateError) {
              console.error('wallUpdateManager: Error updating individual wall', {
                wallId,
                operationId,
                progressive: this.progressiveWalls.has(wallId),
                error: getErrorMessage(wallUpdateError)
              });
              PerformanceMonitor.trackError(wallId, 'wallUpdate', 'individual_update_error');
              updateErrors++;
            }
          }
        } else {
          // Normal mode: update all affected walls with full calculations
          try {
            // Invalidate relevant cache entries
            this.cacheManager.invalidateCacheForWalls(wallIds);

            // Always invalidate joint polygons and outline segments
            // Invalidate joint polygon cache for affected walls
            wallIds.forEach(wallId => {
              this.invalidateJointPolygons(wallId);
              this.invalidateOutlineSegments(wallId);
            });
          } catch (cacheError) {
            console.error('wallUpdateManager: Error invalidating cache', {
              operationId,
              error: getErrorMessage(cacheError),
              wallIds
            });
            PerformanceMonitor.trackError('wallUpdateManager', 'cache_invalidation', 'invalidation_error');
            // Continue with wall updates even if cache invalidation fails
          }

          // Update wall shapes using cached calculations where possible
          for (const wallId of allAffectedWalls) {
            try {
              await this.updateWallShape(wallId);
              normalWallsProcessed++;
            } catch (wallUpdateError) {
              console.error('wallUpdateManager: Error updating wall shape', {
                wallId,
                operationId,
                error: getErrorMessage(wallUpdateError)
              });
              PerformanceMonitor.trackError(wallId, 'wallUpdate', 'shape_update_error');
              updateErrors++;
            }
          }
        }

        const wallUpdateTime = performance.now() - wallUpdateStart;
        PerformanceMonitor.trackDragEvent('wallUpdateManager', 'wall_updates', wallUpdateTime);

        if (wallUpdateTime > 50) {
          // Slow wall updates - warning removed
        }
      } catch (overallWallUpdateError) {
        console.error('wallUpdateManager: Critical error in wall updates', {
          operationId,
          error: getErrorMessage(overallWallUpdateError),
          wallIds,
          progressiveWalls: progressiveWallsProcessed,
          normalWalls: normalWallsProcessed
        });
        PerformanceMonitor.trackError('wallUpdateManager', 'processUpdateGroup', 'critical_wall_update_error');
      }

      // Render update phase
      const renderUpdateStart = performance.now();

      try {
        if (!hasProgressiveWalls) {
          renderStore.finalizeJointGeometryForWalls(
            new Set([...wallIds, ...allAffectedWalls]),
            `wallUpdateManager:${operationId}`,
          );
        }

        this.triggerRenderUpdates(allAffectedWalls);

        const renderUpdateTime = performance.now() - renderUpdateStart;
        PerformanceMonitor.trackDragEvent('wallUpdateManager', 'render_updates', renderUpdateTime);

        if (renderUpdateTime > 10) {
          // Slow render updates - warning removed
        }
      } catch (renderError) {
        console.error('wallUpdateManager: Error in render updates', {
          operationId,
          error: getErrorMessage(renderError),
          affectedWallsCount: allAffectedWalls.size
        });
        PerformanceMonitor.trackError('wallUpdateManager', 'processUpdateGroup', 'render_update_error');
        updateErrors++;
      }

      const totalDuration = performance.now() - startTime;
      if (floorplannerStore.isInitializing) {
        console.log('[load] 2D processUpdateGroup', {
          ms: Math.round(totalDuration),
          walls: wallIds.length,
          affected: allAffectedWalls.size,
          normal: normalWallsProcessed,
        });
      }
      PerformanceMonitor.trackDragEvent('wallUpdateManager', 'total_operation_time', totalDuration);

      // Track memory usage after operation
      const memoryAfter = performance.memory ? performance.memory.usedJSHeapSize / (1024 * 1024) : 0;
      const memoryIncrease = memoryAfter - memoryBefore;

      // Track operation timing
      PerformanceMonitor.trackDragEvent('wallUpdateManager', 'processUpdateGroup', totalDuration);

      // Performance alerts and logging with adaptive thresholds
      const threshold = hasProgressiveWalls ? 20 : 100; // Lower threshold for progressive mode
      if (totalDuration > threshold) {
        // Slow processUpdateGroup operation - this is tracked for metrics but not as an error
        // since it's a performance issue, not a functional failure
        PerformanceMonitor.trackDragEvent('wallUpdateManager', 'slow_operation', totalDuration);
      }

      // Rate-limited memory warnings with higher thresholds and reduced frequency
      const now = Date.now();
      const lastWarning = window.__wallUpdateMemoryWarnings?.lastWarning || 0;
      const warningInterval = 5000; // 5 seconds between warnings (reduced frequency)

      if (memoryIncrease > 30) { // Increased threshold to 30MB to reduce noise (adjusted for Three.js operations)
        if (now - lastWarning > warningInterval) {
          // High memory usage during update group - warning removed
          window.__wallUpdateMemoryWarnings = { lastWarning: now };
        }
      }

      // Symbol attachment monitoring
      const symbolOperations = allAffectedWalls.size - wallIds.length;
      if (symbolOperations > 0) {
        PerformanceMonitor.trackDragEvent('wallUpdateManager', 'symbol_attachment_operations', symbolOperations);
      }

      // Standard performance logging with reduced frequency
      if (totalDuration > 5 || updateErrors > 0) {
        // processUpdateGroup summary - log removed
      }

      // Update error count in stats
      PerformanceMonitor.trackDragEvent('wallUpdateManager', 'update_errors', updateErrors);

    } catch (overallError) {
      const errorContext = {
        operationId,
        error: getErrorMessage(overallError),
        wallIds,
        duration: performance.now() - startTime,
        progressiveWalls: progressiveWallsProcessed,
        normalWalls: normalWallsProcessed,
        updateErrors
      };

      console.error('wallUpdateManager: Critical error in processUpdateGroup', errorContext);
      PerformanceMonitor.trackError('wallUpdateManager', 'processUpdateGroup', 'critical_error');
      PerformanceMonitor.trackDragEvent('wallUpdateManager', 'critical_error', 1);

      // Attempt graceful degradation
      try {
        // Attempting graceful degradation for processUpdateGroup - warning removed
        // Try to trigger basic render updates for the original wall IDs
        if (wallIds.length > 0) {
          this.triggerRenderUpdates(new Set(wallIds));
        }
      } catch (degradationError) {
        console.error('wallUpdateManager: Graceful degradation failed for processUpdateGroup', {
          operationId,
          error: getErrorMessage(degradationError)
        });
        PerformanceMonitor.trackError('wallUpdateManager', 'processUpdateGroup', 'degradation_failed');
      }
    }
  }

  /**
   * Update individual wall shape with progressive mode (simplified for dragging)
   *
   * Progressive mode provides simplified geometry calculations during drag operations.
   * This approach trades visual accuracy for performance by:
   * - Composing basic wall shapes without complex joint calculations
   * - Skipping expensive polygon intersection operations
   * - Deferring all trimming operations until drag completion
   * - Avoiding joint detection and caching overhead
   *
   * Performance logging tracks the efficiency of progressive mode operations.
   */
  private async updateWallShapeProgressive(wallId: string): Promise<void> {
    const startTime = performance.now();
    let progressiveErrors = 0;

    try {
      const wall = floorplannerStore.wallsMap.get(wallId);
      if (!wall) {
        console.warn('wallUpdateManager: Wall not found for progressive update', { wallId });
        PerformanceMonitor.trackError(wallId, 'updateWallShapeProgressive', 'wall_not_found');
        return;
      }

      // Track memory usage before progressive operation
      const memoryBefore = performance.memory ? performance.memory.usedJSHeapSize / (1024 * 1024) : 0;

      try {
        // In progressive mode, compute basic shapes but skip complex joint calculations
        // This keeps walls visible during dragging with significantly better performance
        const shapeCompositionStart = performance.now();

        // Force recomposition by incrementing the wall's lastUpdate to bypass cache
        // This ensures the wall geometry is always fresh during drag operations
        const wall = floorplannerStore.wallsMap.get(wallId);
        if (wall) {
          floorplannerStore.setObjectProperty(wallId, 'lastUpdate', Date.now());
        }

        const innerShapes = renderStore.composeWallShape(wallId);

        const shapeCompositionTime = performance.now() - shapeCompositionStart;
        PerformanceMonitor.trackDragEvent(wallId, 'progressive_shape_composition', shapeCompositionTime);

        if (innerShapes) {
          // Update ALL wall shape maps (not just inner shapes) to ensure complete visual update
          // This includes fill, outlines, and endcaps so the entire wall moves together
          renderStore.innerWallShapesMap.set(wallId, innerShapes);

          // Also update outline shapes if they exist in the renderStore
          // composeWallShape should populate all necessary shape maps
          const outline1 = renderStore.outline1WallShapesMap.get(wallId);
          const outline2 = renderStore.outline2WallShapesMap.get(wallId);
          const endCaps = renderStore.endCapsWallShapesMap.get(wallId);

          // If outlines are already computed, keep them
          // If not, they'll be computed in the next normal update cycle
          if (outline1 && outline2 && endCaps) {
            // Outlines are available, wall is fully up to date
          }
        } else {
          // No inner shapes generated for progressive update - warning removed
          PerformanceMonitor.trackError(wallId, 'updateWallShapeProgressive', 'no_shapes_generated');
          progressiveErrors++;
        }

        if (shapeCompositionTime > 5) {
          // Slow progressive shape composition - warning removed
        }
      } catch (compositionError) {
        console.error('wallUpdateManager: Error in progressive shape composition', {
          wallId,
          error: getErrorMessage(compositionError)
        });
        PerformanceMonitor.trackError(wallId, 'updateWallShapeProgressive', 'composition_error');
        progressiveErrors++;
      }

      // Skip all trimming operations during progressive mode for better performance
      // Complex polygon intersections, line trimming, and shape clipping are deferred
      // Full geometry calculations will be performed when progressive mode is disabled

      // Skip joint detection and caching operations entirely during progressive mode
      // Joint calculations are expensive and not needed for basic visual feedback
      // The joint cache will be properly rebuilt when progressive mode ends

      const totalDuration = performance.now() - startTime;
      PerformanceMonitor.trackDragEvent(wallId, 'progressive_wall_update', totalDuration);

      // Track memory usage after progressive operation
      const memoryAfter = performance.memory ? performance.memory.usedJSHeapSize / (1024 * 1024) : 0;
      const memoryIncrease = memoryAfter - memoryBefore;

      // Performance alerts for progressive mode (should be very fast)
      if (totalDuration > 5) {
        // Slow progressive wall update - warning removed
      }

      // Rate-limited memory warnings for progressive updates
      const now = Date.now();
      const lastWarning = window.__wallUpdateMemoryWarnings?.lastProgressiveWarning || 0;
      const warningInterval = 2000; // 2 seconds between warnings

      if (memoryIncrease > 25) { // Increased threshold to 25MB for single progressive updates (adjusted for Three.js operations)
        if (now - lastWarning > warningInterval) {
          // High memory usage in progressive update - warning removed
          if (!window.__wallUpdateMemoryWarnings) {
            window.__wallUpdateMemoryWarnings = {};
          }
          window.__wallUpdateMemoryWarnings.lastProgressiveWarning = now;
        }
      }

      // Log detailed information for debugging progressive mode efficiency
      if ((totalDuration > 1 || progressiveErrors > 0) && isFloorplannerDebugLoggingEnabled()) {
        console.debug('wallUpdateManager: Progressive wall update summary', {
          wallId,
          duration: totalDuration.toFixed(2) + 'ms',
          errors: progressiveErrors,
          memoryIncrease: memoryIncrease.toFixed(2) + 'MB',
          success: progressiveErrors === 0
        });
      }
    } catch (overallError) {
      const errorContext = {
        wallId,
        error: getErrorMessage(overallError),
        duration: performance.now() - startTime,
        progressiveErrors
      };

      console.error('wallUpdateManager: Critical error in progressive wall update', errorContext);
      PerformanceMonitor.trackError(wallId, 'updateWallShapeProgressive', 'critical_error');

      // Attempt graceful degradation for progressive mode
      try {
        // Attempting graceful degradation for progressive update - warning removed
        // Try to maintain the wall in the progressive set but mark it for normal update later
        // This prevents the wall from disappearing during drag
        renderStore.markWallForFallbackUpdate?.(wallId);
      } catch (degradationError) {
        console.error('wallUpdateManager: Progressive degradation failed', {
          wallId,
          error: getErrorMessage(degradationError)
        });
        PerformanceMonitor.trackError(wallId, 'updateWallShapeProgressive', 'degradation_failed');
      }
    }
  }

  /**
   * Update individual wall shape with caching
   */
  private async updateWallShape(wallId: string): Promise<void> {
    const wall = floorplannerStore.wallsMap.get(wallId);
    if (!wall) return;

    // Always use simplified geometry caching
    if (this.shouldUseCachedSimplifiedGeometry(wallId, wall)) {
      return; // Cache hit, use existing geometry
    }
    // Cache miss or invalid - will compute new simplified geometry

    // Edge mode: compute and cache simplified geometry
    await this.computeAndCacheSimplifiedGeometry(wallId, wall);
  }

  /**
   * Check if simplified geometry cache is valid and should be used.
   * Returns true if cache is valid and can be used, false if we need to rebuild.
   */
  private shouldUseCachedSimplifiedGeometry(wallId: string, wall: any): boolean {
    // Check if cache is valid
    const isValid = wallGeometryCache.isCachedGeometryValid(wallId, wall, floorplannerStore);
    if (isValid) {
      // Increment cache hit counter
      this.simplifiedGeometryCacheHits++;
      return true;
    }
    // Increment cache miss counter
    this.simplifiedGeometryCacheMisses++;
    return false;
  }

  /**
   * Compute and cache simplified geometry for edge connections mode.
   */
  private async computeAndCacheSimplifiedGeometry(wallId: string, wall: any): Promise<void> {
    // Compute new simplified geometry
    const innerShapes = renderStore.composeWallShape(wallId);
    const outline1 = renderStore.outline1WallShapesMap.get(wallId);
    const outline2 = renderStore.outline2WallShapesMap.get(wallId);
    const endCaps = renderStore.endCapsWallShapesMap.get(wallId);

    if (!innerShapes || !outline1 || !outline2 || !endCaps) return;

    // Cache simplified geometry with endpoint data
    wallGeometryCache.setSimplifiedWallGeometry(wallId, wall, floorplannerStore);

    // Commit the cached data to renderStore for immediate use by components
    // This makes WallGeometryCache the single source of truth while keeping
    // renderStore as the runtime data source for rendering
    wallGeometryCache.commitWallToRenderStore(wallId);
  }

  /**
   * Get current wall positions for joint calculation
   */
  private getWallPositionsForJoint(
    wallId: string,
    connectedWallIds: string[]
  ): Map<string, { start: THREE.Vector2; end: THREE.Vector2; wallWidth: number }> {
    const positions = new Map();

    // Add primary wall
    const wall = floorplannerStore.wallsMap.get(wallId);
    if (wall) {
      positions.set(wallId, {
        start: wall.start.clone(),
        end: wall.end.clone(),
        wallWidth: wall.wallWidth || 0
      });
    }

    // Add connected walls
    if (connectedWallIds && connectedWallIds.length > 0) {
      connectedWallIds.forEach(connectedId => {
        const connectedWall = floorplannerStore.wallsMap.get(connectedId);
        if (connectedWall) {
          positions.set(connectedId, {
            start: connectedWall.start.clone(),
            end: connectedWall.end.clone(),
            wallWidth: connectedWall.wallWidth || 0
          });
        }
      });
    }

    return positions;
  }

  /**
   * Apply cached shapes to render store
   */
  private applyCachedShapes(wallId: string, cached: any): void {
    renderStore.innerWallShapesMap.set(wallId, cached.computedShapes.inner);
    renderStore.outline1WallShapesMap.set(wallId, cached.computedShapes.outline1);
    renderStore.outline2WallShapesMap.set(wallId, cached.computedShapes.outline2);
    renderStore.endCapsWallShapesMap.set(wallId, cached.computedShapes.endCaps);
  }

  /**
   * Compute new shapes and cache them
   */
  private async computeAndCacheNewShapes(
    wallId: string,
    wallPositions: Map<string, { start: THREE.Vector2; end: THREE.Vector2; wallWidth: number }>
  ): Promise<void> {
    // Use existing renderStore computation
    const innerShapes = renderStore.composeWallShape(wallId);
    const outline1 = renderStore.outline1WallShapesMap.get(wallId);
    const outline2 = renderStore.outline2WallShapesMap.get(wallId);
    const endCaps = renderStore.endCapsWallShapesMap.get(wallId);

    if (!innerShapes || !outline1 || !outline2 || !endCaps) return;

    // After shape composition is complete for a wall, detect and store joints
    // This ensures joints are detected with the latest geometry before trimming
    try {
      const detectedJoints = renderStore.detectAndStoreJointsForWall(wallId);

      // Log joint detection for debugging (only in development)
      if (detectedJoints.length > 0 && isFloorplannerDebugLoggingEnabled()) {
        console.debug(`wallUpdateManager: Detected ${detectedJoints.length} joints for wall ${wallId}`, {
          jointIds: detectedJoints.map(j => j.id),
          jointTypes: detectedJoints.map(j => j.jointType)
        });
      }
    } catch (jointDetectionError) {
      console.error('wallUpdateManager: Error detecting joints for wall', {
        wallId,
        error: getErrorMessage(jointDetectionError)
      });
      PerformanceMonitor.trackError('wallUpdateManager', 'joint_detection', 'detection_error');
      // Don't fail the entire update if joint detection fails
      // Trimming will proceed with the old joint data or without joint coordination
    }

    // Get connected wall IDs
    const wall = floorplannerStore.wallsMap.get(wallId);
    const connectedWallIds = wall ? this.getConnectedWallIds(wall) : [];

    // Create cache entry
    this.cacheManager.createJointCache(
      wallId,
      connectedWallIds,
      wallPositions,
      {
        inner: innerShapes,
        outline1,
        outline2,
        endCaps
      }
    );

    // Perform trimming operations
    this.performTrimming(wallId);
  }

  /**
   * Perform trimming operations for wall intersections
   *
   * Trimming is skipped for walls in progressive mode to improve performance.
   * Per-wall checking allows some walls to be trimmed while others are in progressive mode.
   * In normal mode, implements two-level trimming to match legacy behavior.
   */
  private performTrimming(wallId: string): void {
    const wall = floorplannerStore.wallsMap.get(wallId);
    if (!wall) return;

    // Skip trimming for this specific wall if it's in progressive mode
    if (this.isWallInProgressiveMode(wallId)) {
      // Trimming operations are deferred for this wall until progressive mode ends
      // This includes polygon intersections, line trimming, and shape clipping
      return;
    }

    // Get connected walls for trimming
    const connectedWalls = this.getConnectedWalls(wall);

    // Trim this wall if it's not in progressive mode
    renderStore.trimShapes(wall);

    // Trim connected walls (level 1) if they're not in progressive mode
    if (connectedWalls.length > 0) {
      connectedWalls.forEach(connectedWall => {
        if (connectedWall && !this.isWallInProgressiveMode(connectedWall.id)) {
          renderStore.trimShapes(connectedWall);

          // Two-level trimming: trim walls connected to the connected walls (level 2)
          // This matches the legacy behavior for normal mode
          const connectedWalls2 = this.getConnectedWalls(connectedWall);
          connectedWalls2.forEach(connectedWall2 => {
            if (connectedWall2 && !this.isWallInProgressiveMode(connectedWall2.id)) {
              renderStore.trimShapes(connectedWall2);
            }
          });
        }
      });
    }
  }

  /**
   * Trigger render updates for affected walls
   */
  private triggerRenderUpdates(wallIds: Set<string>): void {
    if (wallIds.size === 0) {
      return;
    }

    const nonProgressiveWalls: string[] = [];
    let fallbackTarget: string | null = null;

    wallIds.forEach(wallId => {
      renderStore.markWallDirty(wallId);
      floorplannerStore.setObjectProperty(wallId, 'lastUpdate', Date.now());

      if (!this.isWallInProgressiveMode(wallId)) {
        nonProgressiveWalls.push(wallId);
      }

      if (!fallbackTarget) {
        fallbackTarget = wallId;
      }
    });

    if (!this.isProgressiveModeActive() && nonProgressiveWalls.length > 0) {
      nonProgressiveWalls.forEach(wallId => {
        renderStore.requestInvalidation(wallId);
      });
      return;
    }

    if (nonProgressiveWalls.length > 0) {
      renderStore.requestInvalidation(nonProgressiveWalls[0]);
    } else if (fallbackTarget) {
      renderStore.requestInvalidation(fallbackTarget);
    }
  }

  /**
   * Immediately process any pending wall updates instead of waiting for the next batch tick.
   * Invoked after drag/drop operations to eliminate stale queued recomputations.
   */
  async processPendingUpdatesNow(): Promise<void> {
    if (this.isProcessingUpdates) {
      await this.waitForCurrentProcessing();
    }

    if (this.updateBatchTimeout) {
      clearTimeout(this.updateBatchTimeout);
      this.updateBatchTimeout = null;
    }

    await this.processBatchedUpdates();
  }

  /**
   * Handle wall connection changes
   */
  updateWallConnections(wallId: string): void {
    // Invalidate joints in renderStore first
    // This removes stale joint data before updating joint info
    renderStore.invalidateJointsForWall(wallId, 'wallUpdateManager.updateWallConnections', {
      scheduleAffectedWallUpdates: false,
    });

    // Update joint information to invalidate joint caches
    this.updateJointInfo(wallId);

    // Also invalidate joints for all connected walls
    const wall = floorplannerStore.wallsMap.get(wallId);
    if (wall) {
      this.getConnectedWallIds(wall).forEach(connectedId => {
        renderStore.invalidateJointsForWall(connectedId, 'wallUpdateManager.updateWallConnections:connected', {
          scheduleAffectedWallUpdates: false,
        });
      });
    }

    // Rebuild dependency graph for this wall
    if (wall) {
      this.dependencyGraphManager.updateWallDependencies(wallId, wall, new Map(floorplannerStore.wallsMap));
    }

    // Always update edge connection dependencies
    if (wall) {
      this.refreshEdgeConnectionDependencies(wallId);

      // Also refresh edge connection dependencies for edge-connected walls
      if (wall.edgeConnections) {
        wall.edgeConnections.forEach(edgeConn => {
          this.refreshEdgeConnectionDependencies(edgeConn.targetWallId);
        });
      }
    }

    // Reset watchdog timer since we have activity
    this.resetWatchdog();

    // Trigger update
    this.updateWall(wallId, true);
  }

  /**
   * Handle wall position changes
   */
  updateWallPosition(wallId: string): void {
    // Invalidate joints in renderStore first
    renderStore.invalidateJointsForWall(wallId, 'wallUpdateManager.updateWallPosition', {
      scheduleAffectedWallUpdates: false,
    });

    // Update joint information to invalidate joint caches on position changes
    this.updateJointInfo(wallId);

    // Also invalidate for all walls that share a joint with this wall
    const connectedWallIds = this.dependencyGraphManager.getJointConnectedWalls?.(wallId) || [];
    connectedWallIds.forEach(connectedId => {
      renderStore.invalidateJointsForWall(connectedId, 'wallUpdateManager.updateWallPosition:jointConnected', {
        scheduleAffectedWallUpdates: false,
      });
      this.updateJointInfo(connectedId);
    });

    // Update spatial index
    const wall = floorplannerStore.wallsMap.get(wallId);
    if (wall) {
      this.dependencyGraphManager.updateWallDependencies(wallId, wall, new Map(floorplannerStore.wallsMap));
    }

    // Reset watchdog timer since we have activity
    this.resetWatchdog();

    // Trigger update
    this.updateWall(wallId);
  }

  /**
   * Remove a wall from the system
   */
  removeWall(wallId: string): void {
    // Invalidate joints for this wall before removing it
    renderStore.invalidateJointsForWall(wallId);

    // Remove from dependency graph
    this.dependencyGraphManager.removeWall(wallId);

    // Clear cache
    this.cacheManager.invalidateCacheForWalls([wallId]);

    // Clear simplified caches and joint polygons for the removed wall
    // This ensures all traces of the wall are removed from the cache
    wallGeometryCache.clearWall(wallId);

    // Update affected walls
    const affectedWalls = this.dependencyGraphManager.getAffectedWalls(wallId);
    affectedWalls.forEach(affectedId => {
      // Invalidate joints for affected walls too
      renderStore.invalidateJointsForWall(affectedId);
      this.updateWall(affectedId, true);
    });
  }

  /**
   * Get performance statistics
   */
  getStats(): {
    cacheStats: any;
    dependencyStats: any;
    pendingUpdates: number;
  } {
    return {
      cacheStats: this.cacheManager.getCacheStats(),
      dependencyStats: this.dependencyGraphManager.getStats(),
      pendingUpdates: this.pendingUpdates.size
    };
  }

  /**
   * Enable progressive rendering mode for better performance during dragging
   */
  enableProgressiveMode(enabled: boolean): void {
    const wasProgressive = this.isProgressiveMode;
    this.isProgressiveMode = enabled;
    this.progressiveQuality = enabled ? 0.5 : 1.0;
    this.batchDelay = enabled ? 4 : 16; // Much faster updates during drag for better responsiveness

    // When disabling progressive mode, trigger a full redraw of all walls
    if (!enabled && wasProgressive) {
      this.triggerFullRedraw();
    }
  }

  /**
   * Enable progressive rendering mode for a specific wall during dragging
   */
  enableProgressiveModeForWall(wallId: string, enabled: boolean): void {
    const previousSize = this.progressiveWalls.size;

    if (enabled) {
      // Start drag operation monitoring
      const operationId = PerformanceMonitor.startDragOperation(wallId, 'drag');
      this.activeDragOperations.set(wallId, operationId);

      this.progressiveWalls.add(wallId);
      this.batchDelay = 4; // Faster updates during drag
      // Begin deferred batch if this is the first wall
      if (previousSize === 0) {
        renderStore.beginDeferredBatch();
      }
      // Start watchdog to clear stuck progressive walls
      this.startWatchdog();
    } else {
      // End drag operation monitoring
      const operationId = this.activeDragOperations.get(wallId);
      if (operationId) {
        PerformanceMonitor.endDragOperation(operationId);
        this.activeDragOperations.delete(wallId);
      }

      this.progressiveWalls.delete(wallId);

      // Force a normal update for this wall to ensure high-quality geometry
      // This ensures that when dragging ends, the wall is fully recomputed with all details
      this.updateWall(wallId, true);

      // Reset batch delay if no walls are in progressive mode
      if (this.progressiveWalls.size === 0) {
        this.batchDelay = 16;
        // End deferred batch when no walls remain in progressive mode
        renderStore.endDeferredBatch();
        // Stop watchdog when no progressive walls remain
        this.stopWatchdog();
      }
    }
  }

  /**
   * Check if a specific wall is in progressive mode
   */
  isWallInProgressiveMode(wallId: string): boolean {
    return this.progressiveWalls.has(wallId);
  }

  /**
   * Check if any walls are currently in progressive mode
   *
   * This utility method can be used by other components to make progressive mode decisions
   * and optimize their operations when drag operations are active.
   */
  isProgressiveModeActive(): boolean {
    return this.progressiveWalls.size > 0;
  }

  clearProgressiveModeIfIdle(reason: string = 'external_edit'): void {
    if (this.progressiveWalls.size === 0) {
      return;
    }

    if (renderStore.isDragging || DragStateManager.isCurrentlyDragging()) {
      return;
    }

    const progressiveWallIds = Array.from(this.progressiveWalls);
    progressiveWallIds.forEach(wallId => {
      const operationId = this.activeDragOperations.get(wallId);
      if (operationId) {
        PerformanceMonitor.endDragOperation(operationId);
        this.activeDragOperations.delete(wallId);
      }
    });

    this.progressiveWalls.clear();
    this.batchDelay = 16;
    this.stopWatchdog();
    renderStore.endDeferredBatch();
    renderStore.setSafeToRedraw(true);

    if (isFloorplannerDebugLoggingEnabled()) {
      console.debug('wallUpdateManager: cleared idle progressive state', {
        reason,
        wallIds: progressiveWallIds,
      });
    }
  }

  /**
   * Trigger a full redraw of walls that were in progressive mode
   * Note: We now only update walls that were actually in progressive mode,
   * not ALL walls. This prevents cascading updates to unrelated walls which
   * can cause door/window cutout corruption.
   */
  private triggerFullRedraw(): void {
    // Only update walls that were in progressive mode, not ALL walls
    // Previously this updated all walls which caused performance issues
    // and corrupted door/window cutouts on unrelated walls
    const progressiveWallIds = Array.from(this.progressiveWalls);

    // If no progressive walls tracked (shouldn't happen normally),
    // don't update any walls - let the normal update flow handle it
    if (progressiveWallIds.length === 0) {
      return;
    }

    // Update only the walls that were in progressive mode
    progressiveWallIds.forEach(wallId => {
      floorplannerStore.setObjectProperty(wallId, 'lastUpdate', Date.now());

      // Also update their connected walls to ensure corners render correctly
      const wall = floorplannerStore.wallsMap.get(wallId);
      if (wall) {
        wall.edgeConnections?.forEach(conn => {
          floorplannerStore.setObjectProperty(conn.targetWallId, 'lastUpdate', Date.now());
        });
        wall.centerConnections?.forEach(conn => {
          floorplannerStore.setObjectProperty(conn.id, 'lastUpdate', Date.now());
        });
      }
    });
  }

  /**
   * Get joint information for a specific wall
   */
  getJointsForWall(wallId: string): JointInfo[] {
    // Check cache first
    const cached = this.jointInfoCache.get(wallId);
    if (cached) {
      return cached;
    }

    // Get joints from detector
    const joints = this.jointDetector.getJointsForWall(wallId);
    this.jointInfoCache.set(wallId, joints);

    return joints;
  }

  /**
   * Update joint information when wall connections change
   */
  updateJointInfo(wallId: string): void {
    // Invalidate joint cache for this wall and connected walls
    const wall = floorplannerStore.wallsMap.get(wallId);
    if (wall) {
      const connectedWallIds = this.getConnectedWallIds(wall);

      // Clear cache for this wall and all connected walls
      this.jointInfoCache.delete(wallId);
      connectedWallIds.forEach(connectedId => {
        this.jointInfoCache.delete(connectedId);
      });
    }

    // Invalidate joint detector cache
    this.jointDetector.invalidateCache();
  }

  /**
   * Invalidate joint polygons for a wall.
   * Called when a wall is updated to ensure joint polygons are regenerated.
   */
  private invalidateJointPolygons(wallId: string, endpoint?: "start" | "end"): void {

    // Get the wall to find its edge connections
    const wall = floorplannerStore.wallsMap.get(wallId);
    if (!wall || !wall.edgeConnections) return;

    // Collect joint IDs for the wall's relevant edge connections
    const jointIdsToInvalidate: string[] = [];

    wall.edgeConnections.forEach(edgeConn => {
      // If endpoint is specified, only invalidate joints at that endpoint
      if (endpoint && edgeConn.sourceEndpoint !== endpoint) {
        return;
      }

      // Generate joint ID from edge connection
      const jointId = this.generateJointId(wallId, edgeConn);
      jointIdsToInvalidate.push(jointId);
    });

    // Clear each specific joint polygon
    jointIdsToInvalidate.forEach(jointId => {
      wallGeometryCache.clearJoint(jointId);
    });
  }

  /**
   * Invalidate outline segments for a wall.
   * Called when a wall is updated to ensure outline segments are regenerated.
   */
  private invalidateOutlineSegments(wallId: string): void {

    // Clear outline segments using centralized cache manager
    wallGeometryCache.clearOutlineSegmentsForWall(wallId);
  }

  /**
   * Generate a unique joint ID from wall and edge connection.
   */
  private generateJointId(wallId: string, edgeConn: WallEdgeConnection): string {
    // Create a deterministic ID based on the participating wall IDs
    const wallIds = [wallId, edgeConn.targetWallId].sort();
    return `joint_${wallIds[0]}_${wallIds[1]}_${edgeConn.sourceEndpoint}_${edgeConn.targetEndpoint}`;
  }

  /**
   * Invalidate wall cache when a specific endpoint changes.
   * More efficient than full wall invalidation when only one endpoint moves.
   */
  invalidateWallByEndpointChange(wallId: string, endpoint: "start" | "end"): void {
    // Use endpoint-specific invalidation for better performance
    // Invalidate simplified geometry cache
    const wall = floorplannerStore.wallsMap.get(wallId);
    if (!wall) return;

    // Clear simplified geometry cache for this wall
    wallGeometryCache.clearSimplifiedGeometry(wallId);

    // Clear outline segments cache
    wallGeometryCache.clearOutlineSegmentsForWall(wallId);

    // Invalidate joint polygons at the affected endpoint
    this.invalidateJointPolygons(wallId, endpoint);

    // Ensure geometry version is incremented before scheduling update
    // Use centralized version increment method
    floorplannerStore.incrementWallGeometryVersion(wallId);

    // Always call updateWall to schedule recomputation
    this.updateWall(wallId);

    // Update dependency graph if needed (edge connections might have changed)
    if (wall.edgeConnections) {
      // For each edge connection at the affected endpoint
      wall.edgeConnections.forEach(edgeConn => {
        if (edgeConn.sourceEndpoint === endpoint) {
          // This endpoint's connection might have changed
          // Force a wall update to rebuild geometry
          this.updateWall(wallId);
        }
      });
    }
  }

  /**
   * Get all joints in the current floorplan
   */
  getAllJoints(): Map<string, JointInfo> {
    return this.jointDetector.detectAllJoints();
  }

  /**
   * Check if a wall participates in complex joints that need special handling
   */
  wallHasComplexJoints(wallId: string): boolean {
    const joints = this.getJointsForWall(wallId);
    return joints.some(joint => joint.needsSpecialHandling);
  }

  /**
   * Get joint detection statistics
   */
  getJointStats(): {
    totalJoints: number;
    jointTypes: Record<string, number>;
    complexJoints: number;
    cacheStats: any;
  } {
    const allJoints = this.getAllJoints();
    const jointTypes: Record<string, number> = {};
    let complexJoints = 0;

    allJoints.forEach(joint => {
      jointTypes[joint.jointType] = (jointTypes[joint.jointType] || 0) + 1;
      if (joint.needsSpecialHandling) {
        complexJoints++;
      }
    });

    return {
      totalJoints: allJoints.size,
      jointTypes,
      complexJoints,
      cacheStats: this.jointDetector.getCacheStats()
    };
  }

  /**
   * Start watchdog timer to detect stuck progressive walls
   */
  private startWatchdog(): void {
    this.stopWatchdog(); // Clear any existing timer
    if (this.progressiveWalls.size > 0) {
      this.watchdogTimer = window.setTimeout(() => {
        this.handleWatchdogTimeout();
      }, this.watchdogTimeout);
    }
  }

  /**
   * Stop watchdog timer
   */
  private stopWatchdog(): void {
    if (this.watchdogTimer) {
      clearTimeout(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }

  /**
   * Handle watchdog timeout - clear stuck progressive walls
   */
  private handleWatchdogTimeout(): void {
    const timeoutStart = performance.now();

    if (this.progressiveWalls.size > 0) {
      const stuckWalls = Array.from(this.progressiveWalls);
      const stuckDuration = this.watchdogTimeout;

      // Watchdog detected stuck progressive walls - warning removed
      // Track watchdog timeout as performance issue
      PerformanceMonitor.trackError('wallUpdateManager', 'watchdog', 'timeout_detected');

      // Clear all progressive walls
      this.progressiveWalls.clear();

      try {
        // End deferred batch to prevent stuck state
        renderStore.endDeferredBatch();
      } catch (batchError) {
        console.error('wallUpdateManager: Error ending deferred batch during watchdog timeout', {
          error: getErrorMessage(batchError)
        });
        PerformanceMonitor.trackError('wallUpdateManager', 'watchdog', 'batch_error');
      }

      // Reset batch delay
      this.batchDelay = 16;

      try {
        // Trigger a full redraw to ensure simplified shapes are replaced with accurate geometry
        this.triggerFullRedraw();
      } catch (redrawError) {
        console.error('wallUpdateManager: Error triggering full redraw during watchdog timeout', {
          error: getErrorMessage(redrawError)
        });
        PerformanceMonitor.trackError('wallUpdateManager', 'watchdog', 'redraw_error');
      }

      // Track watchdog recovery performance
      const recoveryTime = performance.now() - timeoutStart;
      PerformanceMonitor.trackDragEvent('wallUpdateManager', 'watchdog_recovery', recoveryTime);

      // Watchdog timeout recovery completed - log removed
    }

    this.watchdogTimer = null;
  }

  /**
   * Reset watchdog timer (called when updates are received)
   */
  private resetWatchdog(): void {
    if (this.progressiveWalls.size > 0) {
      this.startWatchdog();
    }
  }


  /**
   * Get performance report for drag operations
   */
  getPerformanceReport(): object {
    const now = performance.now();
    const performanceMetrics = PerformanceMonitor.getMetrics();
    const performanceAnalysis = PerformanceMonitor.analyzePerformance();

    return {
      timestamp: now,
      performanceMetrics,
      performanceAnalysis,

      // Manager-specific state
      managerState: {
        pendingUpdates: this.pendingUpdates.size,
        isProcessingUpdates: this.isProcessingUpdates,
        watchdogActive: this.watchdogTimer !== null,
        activeProgressiveWalls: this.progressiveWalls.size,
        progressiveWallIds: Array.from(this.progressiveWalls),
        wallUpdateFrequencies: Object.fromEntries(this.wallUpdateFrequencies),
        activeDragOperations: this.activeDragOperations.size,
        cacheStats: this.jointDetector.getCacheStats(),

        // Cache performance metrics
        cachePerformance: {
          simplifiedGeometry: {
            hits: this.simplifiedGeometryCacheHits,
            misses: this.simplifiedGeometryCacheMisses,
            hitRate: this.simplifiedGeometryCacheHits / Math.max(1, this.simplifiedGeometryCacheHits + this.simplifiedGeometryCacheMisses)
          },
          jointPolygons: {
            hits: this.jointPolygonCacheHits,
            misses: this.jointPolygonCacheMisses,
            hitRate: this.jointPolygonCacheHits / Math.max(1, this.jointPolygonCacheHits + this.jointPolygonCacheMisses)
          },
          outlineSegments: {
            hits: this.outlineSegmentCacheHits,
            misses: this.outlineSegmentCacheMisses,
            hitRate: this.outlineSegmentCacheHits / Math.max(1, this.outlineSegmentCacheHits + this.outlineSegmentCacheMisses)
          }
        }
      }
    };
  }

  /**
   * Get drag operation metrics specifically for performance monitoring
   */
  getDragOperationMetrics(): object {
    const performanceMetrics = PerformanceMonitor.getMetrics();
    const performanceAnalysis = PerformanceMonitor.analyzePerformance();

    return {
      // Global performance metrics
      performanceMetrics,
      performanceAnalysis,

      // Manager-specific metrics
      managerMetrics: {
        wallUpdateFrequencies: Object.fromEntries(this.wallUpdateFrequencies),
        activeProgressiveWalls: this.progressiveWalls.size,
        activeDragOperations: this.activeDragOperations.size,
        pendingUpdates: this.pendingUpdates.size
      }
    };
  }

  /**
   * Detect performance issues and suggest optimizations
   */
  detectPerformanceIssues(): string[] {
    const issues: string[] = [];
    const bottlenecks = PerformanceMonitor.detectBottlenecks();

    // Include global performance bottlenecks
    issues.push(...bottlenecks);

    // Manager-specific checks
    // Check for stuck progressive walls
    if (this.progressiveWalls.size > 5) {
      issues.push(`High number of progressive walls: ${this.progressiveWalls.size}`);
    }

    // Check pending updates backlog
    if (this.pendingUpdates.size > 10) {
      issues.push(`Large pending updates backlog: ${this.pendingUpdates.size}`);
    }

    // Check wall update frequency (potential hotspots)
    const highFrequencyWalls = Array.from(this.wallUpdateFrequencies.entries())
      .filter(([_, frequency]) => frequency > 100);
    if (highFrequencyWalls.length > 0) {
      issues.push(`High frequency update walls: ${highFrequencyWalls.map(([id, freq]) => `${id} (${freq} updates)`).join(', ')}`);
    }

    return issues;
  }

  /**
   * Reset performance metrics for fresh analysis
   */
  resetPerformanceMetrics(): void {
    // Reset manager-specific metrics
    this.wallUpdateFrequencies.clear();
    this.activeDragOperations.clear();

    // Reset cache performance metrics
    this.simplifiedGeometryCacheHits = 0;
    this.simplifiedGeometryCacheMisses = 0;
    this.jointPolygonCacheHits = 0;
    this.jointPolygonCacheMisses = 0;
    this.outlineSegmentCacheHits = 0;
    this.outlineSegmentCacheMisses = 0;

    // Reset global performance monitor
    PerformanceMonitor.reset();

    // Performance metrics reset - log removed
  }

  /**
   * Clear all data and reset
   */
  clear(): void {
    this.cacheManager.clearCache();
    this.dependencyGraphManager.clear();
    this.pendingUpdates.clear();
    this.jointInfoCache.clear();
    this.progressiveWalls.clear();
    this.jointDetector.invalidateCache();
    this.stopWatchdog();
    if (this.updateBatchTimeout) {
      clearTimeout(this.updateBatchTimeout);
      this.updateBatchTimeout = null;
    }

    // Always clear edge connection caches
    renderStore.jointPolygonsMap.clear();
    renderStore.outlineSegmentsMap.clear();

    // Clear performance monitoring data
    this.resetPerformanceMetrics();
  }
}

// Global instance
export const wallUpdateManager = new WallUpdateManager();
